import React, { useEffect } from 'react'
import { useHistory, useLocation, useQueryParams } from '@fs/zion-router'
import { type PID } from '@fs/zion-tree-types'
import { useContainerWidth } from '@fs/zion-ui'
import { useLastViewedPersonView } from '@fs/zion-tree-groups'
import { vitalTypes } from '../person-page/PersonPage'
import { usePersonPageParams, generatePersonPageUrl, type PersonSubPage } from '../person-page/personPageRouteUtil'
import { RedirectToStartingPid } from './RedirectToStartingPid'
import PersonPageSkeleton from '../person-page/PersonPageSkeleton'

export default function RedirectToPersonPageSubPage(): JSX.Element | null {
  const { pid, subPage } = usePersonPageParams('partial')
  const atWidth = useContainerWidth()
  const { search } = useLocation()
  const { loading, view } = useLastViewedPersonView()
  const { query } = useQueryParams()
  const history = useHistory()
  // openDetail is for backwards compatibility with old v8 links
  const { conclusionQuery = query.openDetail } = query

  return (
    <RedirectToPersonPageSubPageContent
      {...{ atWidth, search, subPage, pid, loading, conclusionQuery, history }}
      view={view as PersonSubPage | null | undefined}
    />
  )
}

type Props = {
  atWidth: ReturnType<typeof useContainerWidth>
  history: ReturnType<typeof useHistory>
  search: string
  subPage?: PersonSubPage
  view?: PersonSubPage | null
  pid?: PID
  loading?: boolean
  conclusionQuery?: string
}
// exported for testing purposes
export function RedirectToPersonPageSubPageContent({
  atWidth,
  history,
  search,
  subPage,
  view,
  pid,
  loading,
  conclusionQuery = '',
}: Props): JSX.Element {
  const defaultView = atWidth({ lg: 'details', default: 'vitals' })
  const isAutoOpenConclusion = search.includes('conclusion')
  const isAutoOpenCoupleRelationship = search.includes('coupleRelationshipId')

  let searchOverrideView: 'details' | 'vitals' | 'non-vitals' | 'family' | undefined

  if (defaultView === 'vitals') {
    if (isAutoOpenConclusion) {
      if (vitalTypes.includes(conclusionQuery)) {
        searchOverrideView = defaultView
      } else {
        searchOverrideView = 'non-vitals'
      }
    }
    if (isAutoOpenCoupleRelationship) {
      searchOverrideView = 'family'
    }
  } else if (isAutoOpenConclusion || isAutoOpenCoupleRelationship) {
    searchOverrideView = defaultView
  }

  const targetSubPage = searchOverrideView ?? subPage ?? view ?? defaultView
  const targetUrl = pid ? generatePersonPageUrl(targetSubPage, pid, search) : null

  useEffect(() => {
    if (targetUrl) history.replace(targetUrl)
  }, [targetUrl, history])

  if (!subPage && loading) return <PersonPageSkeleton />
  if (pid) return <PersonPageSkeleton />
  return <RedirectToStartingPid subPage={targetSubPage} />
}
